/**
 * Minimum-transfer settlement — pure function, no Firestore.
 *
 * Given a list of balances (from calculateTripBalances), returns the fewest
 * transactions that zero everyone out.
 *
 * Algorithm (greedy min-transfers):
 *   1. Split into creditors (net > 0) and debtors (net < 0).
 *   2. Pair the largest debtor with the largest creditor.
 *   3. The transfer is min(|debt|, credit); apply and re-queue whoever isn't
 *      zero yet.
 *   4. Repeat until both queues are empty (within 1-cent tolerance).
 *
 * O(N log N) for sort + N iterations. Deterministic — same input yields the
 * same output, so the UI stays stable across renders.
 */

import type { ExpenseEntry } from "./types";

export interface Settlement {
  fromUserId: string;
  fromName: string;
  toUserId: string;
  toName: string;
  amount: number;
}

/**
 * Pure (synchronous) balance calculation — takes the expenses array directly.
 * Returns one entry per user: { userId, name, paid, owes, net }.
 *
 * Payer resolution: `payerUid ?? creatorId`. Old rows without payerUid
 * behave exactly as before (creator is treated as payer), so no data
 * migration is required. New rows may set a distinct payer to record
 * "Marta paid, you logged it" flows.
 */
export function calculateTripBalances(
  expenses: ExpenseEntry[],
): { userId: string; name: string; paid: number; owes: number; net: number }[] {
  const balances: Record<
    string,
    { name: string; paid: number; owes: number }
  > = {};

  for (const expense of expenses) {
    const payerId = expense.payerUid ?? expense.creatorId;
    const payerName = expense.payerName ?? expense.creatorName;
    if (!balances[payerId]) {
      balances[payerId] = {
        name: payerName,
        paid: 0,
        owes: 0,
      };
    }
    balances[payerId]!.paid += expense.amount;

    for (const split of expense.splits) {
      if (!balances[split.userId]) {
        balances[split.userId] = {
          name: split.userName,
          paid: 0,
          owes: 0,
        };
      }
      balances[split.userId]!.owes += split.amount;
    }
  }

  return Object.entries(balances).map(([userId, b]) => ({
    userId,
    name: b.name,
    paid: b.paid,
    owes: b.owes,
    net: b.paid - b.owes,
  }));
}

interface Balance {
  userId: string;
  name: string;
  net: number;
}

const EPS = 0.005; // ½ cent — anything smaller counts as "settled"

export function computeSettlements(balances: readonly Balance[]): Settlement[] {
  const creditors = balances
    .filter((b) => b.net > EPS)
    .map((b) => ({ ...b, remaining: b.net }))
    .sort((a, b) => b.remaining - a.remaining);
  const debtors = balances
    .filter((b) => b.net < -EPS)
    .map((b) => ({ ...b, remaining: -b.net }))
    .sort((a, b) => b.remaining - a.remaining);

  const out: Settlement[] = [];
  let i = 0;
  let j = 0;

  while (i < debtors.length && j < creditors.length) {
    const debtor = debtors[i]!;
    const creditor = creditors[j]!;
    const amount = Math.round(Math.min(debtor.remaining, creditor.remaining) * 100) / 100;
    if (amount > 0) {
      out.push({
        fromUserId: debtor.userId,
        fromName: debtor.name,
        toUserId: creditor.userId,
        toName: creditor.name,
        amount,
      });
    }
    debtor.remaining -= amount;
    creditor.remaining -= amount;
    if (debtor.remaining < EPS) i++;
    if (creditor.remaining < EPS) j++;
  }

  return out;
}

export interface PersonalBalance {
  userId: string;
  name: string;
  /** Always positive. */
  amount: number;
  /** "owes-you": they pay the viewer. "you-owe": the viewer pays them. */
  direction: "owes-you" | "you-owe";
}

/**
 * The viewer's own debts and credits, derived from the settle-up plan.
 *
 * A member's *group* net says nothing about what they owe *you*: if Bob paid
 * €60 for Carol, Bob's net is +€60 but Alice (uninvolved) owes him nothing.
 * Reading other members' nets as pairwise balances made users "owe" people
 * they had no shared expenses with. Only transfers that involve the viewer
 * belong in their personal view.
 */
export function personalBalances(
  settlements: readonly Settlement[],
  viewerUid: string | null | undefined,
): PersonalBalance[] {
  if (!viewerUid) return [];
  const byUser = new Map<string, { name: string; net: number }>();
  const add = (userId: string, name: string, delta: number) => {
    const cur = byUser.get(userId) ?? { name, net: 0 };
    cur.net += delta;
    byUser.set(userId, cur);
  };
  for (const s of settlements) {
    if (s.toUserId === viewerUid) add(s.fromUserId, s.fromName, s.amount);
    else if (s.fromUserId === viewerUid) add(s.toUserId, s.toName, -s.amount);
  }
  return [...byUser.entries()]
    .filter(([, v]) => Math.abs(v.net) > EPS)
    .map(([userId, v]) => ({
      userId,
      name: v.name,
      amount: Math.round(Math.abs(v.net) * 100) / 100,
      direction: v.net > 0 ? ("owes-you" as const) : ("you-owe" as const),
    }))
    .sort((a, b) => b.amount - a.amount);
}

/**
 * Who owes whom *directly*: each split participant owes the expense's payer
 * their share, and recorded payments (payer → recipient) pay that back.
 * Debts are netted per pair only — never across the group.
 *
 * This is what people expect to see. The minimum-transfer plan above
 * (computeSettlements) nets across the whole group, so it can route your
 * debt to someone you never shared an expense with: Alice pays lunch for
 * Alice+Bob, Bob pays a taxi for Bob+Carol → the plan says "Carol → Alice",
 * while Carol only ever shared a taxi with Bob.
 */
export function computeDirectDebts(
  expenses: readonly Pick<ExpenseEntry, "creatorId" | "creatorName" | "payerUid" | "payerName" | "splits">[],
): Settlement[] {
  // Keyed "a|b" with a < b; positive = a owes b.
  const pairs = new Map<string, number>();
  const names = new Map<string, string>();

  for (const e of expenses) {
    const payer = e.payerUid ?? e.creatorId;
    names.set(payer, e.payerName ?? e.creatorName);
    for (const s of e.splits) {
      names.set(s.userId, s.userName);
      if (s.userId === payer || s.amount === 0) continue;
      const [a, b, sign] = s.userId < payer ? [s.userId, payer, 1] : [payer, s.userId, -1];
      const key = `${a}|${b}`;
      pairs.set(key, (pairs.get(key) ?? 0) + sign * s.amount);
    }
  }

  const out: Settlement[] = [];
  for (const [key, owed] of pairs) {
    const amount = Math.round(Math.abs(owed) * 100) / 100;
    if (amount < 0.01) continue;
    const [a, b] = key.split("|") as [string, string];
    const [from, to] = owed > 0 ? [a, b] : [b, a];
    out.push({
      fromUserId: from,
      fromName: names.get(from) ?? from,
      toUserId: to,
      toName: names.get(to) ?? to,
      amount,
    });
  }
  return out.sort((x, y) => y.amount - x.amount || x.fromName.localeCompare(y.fromName));
}
